import { CardBrand, CardExpiryStatus, Feature, PaymentType } from '@afterpay/types'
import { differenceInMonths, lastDayOfMonth } from 'date-fns'
import { CardErrorReason, EnrichedCardDetails, PaymentMethodType } from '../model'
import { PaymentCreditCardType, CardDetails } from '@afterpaytouch/portal-api'

export const PCL_CARD_EXPIRY_LIMIT_IN_MONTHS = 1

export const convertExpiryDateFromString = (expiryString: string): Date => {
  const match = /(\d{1,2})\/(\d{2})/.exec(expiryString)
  if (match === null) {
    // @ts-expect-error: OPERATION BLEED STOPPER
    return null
  }

  const [month, year] = match.slice(1, 3).map((s) => parseInt(s, 10))
  if (month < 1 || month > 12) {
    // @ts-expect-error: OPERATION BLEED STOPPER
    return null
  }

  return lastDayOfMonth(new Date(2000 + year, month - 1))
}

export const cvvIsValid = (cvv: string, cardBrand: CardBrand): boolean => {
  return /^\d+$/.test(cvv) && cvv.length === (cardBrand === CardBrand.AMEX ? 4 : 3)
}

export type PreferredCard =
  | {
      card: EnrichedCardDetails | null
      valid: false
      switched: boolean
      reason: CardErrorReason | null
    }
  | {
      card: EnrichedCardDetails
      valid: true
      switched: boolean
      reason: CardErrorReason | null
    }

export function isNonNullPreferredCard(card: EnrichedCardDetails | null | undefined): card is EnrichedCardDetails {
  return (card as EnrichedCardDetails) !== null
}

const compareCards = (x: CardDetails, y: CardDetails): number => {
  return new Date(x.expiryYear, x.expiryMonth - 1) > new Date(y.expiryYear, y.expiryMonth - 1) ? 1 : -1
}

type EnrichCard = (params: {
  enabledCardTypes: CardBrand[]
  lastPaymentDate?: Date
  cardExpiryLimitInMonths?: number
  paymentType?: PaymentType
  feature?: Feature
}) => (card: CardDetails) => EnrichedCardDetails

export const enrichCard: EnrichCard = ({ enabledCardTypes, lastPaymentDate = new Date(), cardExpiryLimitInMonths = 0, paymentType, feature }) => {
  const validateBrand = cardValidBrand(enabledCardTypes)
  const validateExpiry = cardValidExpiry(lastPaymentDate, cardExpiryLimitInMonths)
  const validateExpiryLimitInMonths = validateExpiryByMonths(cardExpiryLimitInMonths)
  return (card) => ({
    ...card,
    paymentMethodType: PaymentMethodType.CREDIT_CARD,
    ...(cardExpiryLimitInMonths > 0 && { cardExpiryLimitInMonths }),
    error: cardAllowed(card, paymentType, feature) ?? validateBrand(card) ?? validateExpiryLimitInMonths(card) ?? validateExpiry(card),
  })
}

export const enrichCardTopazResult = (card: EnrichedCardDetails, error?: CardErrorReason): EnrichedCardDetails => {
  return {
    ...card,
    paymentMethodType: PaymentMethodType.CREDIT_CARD,
    error: error ?? null,
  }
}

export const filterCardsForPreferredForOverduePayments = (savedCards: CardDetails[]): CardDetails | null => {
  const preferredCards = savedCards.filter((card) => Boolean(card?.preferred && card.cardExpiryStatus !== CardExpiryStatus.EXPIRED))

  if (preferredCards.length === 0) {
    return null
  }

  return preferredCards[0]
}

export const selectPreferredCard = (preferredCard: EnrichedCardDetails | null, savedCards: EnrichedCardDetails[]): PreferredCard => {
  const fallback =
    savedCards
      .filter((card) => card.error === null)
      .sort(compareCards)
      .pop() ?? null
  if (preferredCard === null) {
    // @ts-expect-error: OPERATION BLEED STOPPER
    return {
      card: fallback,
      switched: false,
      valid: fallback?.error === null,
      reason: fallback?.error ?? null,
    }
  }
  if (preferredCard.error === null) {
    return {
      card: preferredCard,
      switched: false,
      valid: true,
      reason: null,
    }
  }
  if (fallback !== null) {
    return {
      card: fallback,
      switched: true,
      valid: true,
      reason: preferredCard.error,
    }
  }
  return {
    card: preferredCard,
    switched: false,
    valid: false,
    reason: preferredCard.error,
  }
}

const cardValidBrand =
  (enabledCards: CardBrand[]) =>
  (card: CardDetails): Extract<CardErrorReason, 'invalidBrand'> | null =>
    enabledCards.includes(card?.cardBrand) ? null : 'invalidBrand'

const cardValidExpiry =
  (lastPaymentDate: Date, cardExpiryLimitInMonths: number) =>
  (card: CardDetails): Extract<CardErrorReason, 'cardExpiry'> | null => {
    if (cardExpiryLimitInMonths !== 0) {
      return null
    }

    if (card === undefined || card === null) {
      return 'cardExpiry'
    }
    const cardExpiryDate = new Date(card.expiryYear, card.expiryMonth)
    return cardExpiryDate >= lastPaymentDate ? null : 'cardExpiry'
  }

export const isCashAppCard = (card: CardDetails): boolean => {
  return card.creditCardType === PaymentCreditCardType.CASH_APP_CARD
}

interface ValidationContext {
  paymentType?: PaymentType
  feature?: Feature
}

type CardValidationStrategy = (
  card: CardDetails,
  context?: ValidationContext
) => Extract<CardErrorReason, 'creditCardNotAllowed' | 'fallbackGenericMessage' | 'cashAppCardIneligible'> | null

const cardValidationStrategies: Record<string, CardValidationStrategy> = {
  cashAppCard: (card: CardDetails, { paymentType, feature }: ValidationContext) => {
    if (paymentType === PaymentType.PCL) {
      return 'creditCardNotAllowed'
    }

    if (feature === Feature.AFF) {
      return 'creditCardNotAllowed'
    }

    if (card.allowed === false) {
      return 'cashAppCardIneligible'
    }

    return null
  },

  pclOrAff: (card: CardDetails, { paymentType, feature }: ValidationContext) => {
    if ((paymentType === PaymentType.PCL || feature === Feature.AFF) && (card.creditCardType === PaymentCreditCardType.CREDIT || card.allowed === false)) {
      return 'creditCardNotAllowed'
    }

    return null
  },

  default: (card: CardDetails) => {
    if (card.allowed === false) {
      return 'fallbackGenericMessage'
    }

    return null
  },
}

const cardAllowed = (
  card: CardDetails,
  paymentType?: PaymentType,
  feature?: Feature
): Extract<CardErrorReason, 'creditCardNotAllowed' | 'fallbackGenericMessage' | 'cashAppCardIneligible'> | null => {
  const context: ValidationContext = { paymentType, feature }

  if (isCashAppCard(card)) {
    return cardValidationStrategies.cashAppCard(card, context)
  }

  return cardValidationStrategies.pclOrAff(card, context) ?? cardValidationStrategies.default(card)
}

const validateExpiryByMonths =
  (cardExpiryLimitInMonths: number) =>
  (card: CardDetails): Extract<CardErrorReason, 'cardExpiryLimitInMonths'> | null => {
    if (cardExpiryLimitInMonths === 0) {
      return null
    }

    const cardExpiryDate = new Date(card.expiryYear, card.expiryMonth)
    const monthsBetweenDates = differenceInMonths(cardExpiryDate, new Date())
    return monthsBetweenDates > cardExpiryLimitInMonths ? null : 'cardExpiryLimitInMonths'
  }
